import React, { useEffect, useRef } from 'react';
import { Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import ReactQuill from 'react-quill';
import { InsuranceSelectSubmissionForm } from 'modules/vendorOpportunity/insurance/types';

export const InsuranceCoverageRequirements = () => {
  const classes = useStyles();
  const { control } = useFormContext<InsuranceSelectSubmissionForm>();

  const skipNextCoveragePaste = useRef(false);

  const coverageRequirements = useWatch({ control, name: 'coverageRequirements' });
  const certificateRequirements = useWatch({ control, name: 'certificateRequirements' });
  const otherInstructions = useWatch({ control, name: 'otherInstructions' });

  const coverageRef = useRef<ReactQuill | null>(null);
  const certificateRef = useRef<ReactQuill | null>(null);
  const otherRef = useRef<ReactQuill | null>(null);

  const pushHtml = (ref: React.MutableRefObject<ReactQuill | null>, html: string | undefined) => {
    const editor = ref.current?.getEditor?.();
    if (!editor) return;

    const current = editor.root.innerHTML ?? '';
    const next = html ?? '';

    // Only push if different (prevents cursor jumping while typing)
    if (current !== next) {
      editor.clipboard.dangerouslyPasteHTML(next);
    }
  };

  useEffect(() => {
    if (skipNextCoveragePaste.current) {
      skipNextCoveragePaste.current = false;
      return;
    }
    pushHtml(coverageRef, typeof coverageRequirements === 'string' ? coverageRequirements : '');
  }, [coverageRequirements]);

  useEffect(() => {
    if (skipNextCoveragePaste.current) {
      skipNextCoveragePaste.current = false;
      return;
    }
    pushHtml(certificateRef, typeof certificateRequirements === 'string' ? certificateRequirements : '');
  }, [certificateRequirements]);

  useEffect(() => {
    if (skipNextCoveragePaste.current) {
      skipNextCoveragePaste.current = false;
      return;
    }
    pushHtml(otherRef, typeof otherInstructions === 'string' ? otherInstructions : '');
  }, [otherInstructions]);

  return (
    <Stack gap="1rem">
      <Stack>
        <Typography component="span" variant="body1">
          Coverage Requirements:
        </Typography>
        <Controller
          name="coverageRequirements"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <ReactQuill
              ref={instance => {
                coverageRef.current = instance;
              }}
              className={classes.reactQuill}
              defaultValue={typeof field.value === 'string' ? field.value : ''}
              placeholder="Physical Damage Insurance is required..."
              onChange={(content, _delta, source) => {
                if (source === 'user') {
                  skipNextCoveragePaste.current = true;
                  field.onChange(content);
                }
              }}
              onBlur={field.onBlur}
            />
          )}
        />
      </Stack>

      <Stack>
        <Typography component="span" variant="body1">
          Certificate Requirements:
        </Typography>
        <Controller
          name="certificateRequirements"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <ReactQuill
              ref={instance => {
                certificateRef.current = instance;
              }}
              className={classes.reactQuill}
              defaultValue={typeof field.value === 'string' ? field.value : ''}
              placeholder="Certificate must list [Lender Name]..."
              onChange={(content, _delta, source) => {
                if (source === 'user') {
                  skipNextCoveragePaste.current = true;
                  field.onChange(content);
                }
              }}
              onBlur={field.onBlur}
            />
          )}
        />
      </Stack>

      <Stack>
        <Typography component="span" variant="body1">
          Other Instructions:
        </Typography>
        <Controller
          name="otherInstructions"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <ReactQuill
              ref={instance => {
                otherRef.current = instance;
              }}
              className={classes.reactQuill}
              defaultValue={typeof field.value === 'string' ? field.value : ''}
              placeholder="Electronic signature is required..."
              onChange={(content, _delta, source) => {
                if (source === 'user') {
                  skipNextCoveragePaste.current = true;
                  field.onChange(content);
                }
              }}
              onBlur={field.onBlur}
            />
          )}
        />
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles({
  reactQuill: {
    '& .ql-container': {
      minHeight: '80px',
    },
  },
});
